import React, { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useNavigate, useParams } from "react-router-dom";
import { getApiErrorMessage } from "../../store/api/baseApi";
import {
  ProjectPayload,
  useCreateProjectMutation,
  useFetchProjectByIdQuery,
  useFetchProjectContractsQuery,
  useUpdateProjectMutation,
  useUploadProjectContractMutation,
  useDeleteProjectContractMutation,
} from "../../store/api/projectsApi";
import { ProjectStatus } from "./adminData";
import "./admin.css";

const initialForm: ProjectPayload = {
  title: "",
  owner: "",
  location: "",
  area: "",
  rateOfConstruction: "",
  description: "",
  status: "pending" as ProjectStatus,
  contractFiles: [],
};

const steps = ["Project Basics", "Location And Cost", "Contract Upload"];

const ProjectCreatePage = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const isEditing = Boolean(projectId);

  const { data: editingProject, isLoading: isFetchingProject, error: fetchError } =
    useFetchProjectByIdQuery(projectId ?? "", { skip: !projectId });

  const { data: serverContracts = [] } = useFetchProjectContractsQuery(
    projectId ?? "",
    { skip: !projectId }
  );

  const [createProject, { isLoading: isCreating }] = useCreateProjectMutation();
  const [updateProject, { isLoading: isUpdating }] = useUpdateProjectMutation();
  const [uploadContract, { isLoading: isUploadingContract }] = useUploadProjectContractMutation();
  const [deleteContract] = useDeleteProjectContractMutation();

  const [currentStep, setCurrentStep] = useState(0);
  const [form, setForm] = useState<ProjectPayload>(initialForm);
  const [newContractFiles, setNewContractFiles] = useState<File[]>([]);
  const [submitError, setSubmitError] = useState("");
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const contractInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!projectId) {
      setForm(initialForm);
      setNewContractFiles([]);
      setCurrentStep(0);
      return;
    }
    if (!editingProject) return;

    setForm({
      title: editingProject.title,
      owner: editingProject.owner,
      location: editingProject.location,
      area: editingProject.area,
      rateOfConstruction: editingProject.rateOfConstruction,
      description: editingProject.description,
      status: editingProject.status,
      contractFiles: [],
    });
    setNewContractFiles([]);
    setCurrentStep(0);
  }, [editingProject, projectId]);

  const stepValid = useMemo(() => {
    if (currentStep === 0) {
      return form.title.trim() && form.owner.trim() && form.description.trim();
    }
    if (currentStep === 1) {
      return form.location.trim() && form.area.trim() && form.rateOfConstruction.trim();
    }
    return true;
  }, [currentStep, form]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError("");
    setUploadErrors([]);

    try {
      let savedProjectId: string;

      if (isEditing && projectId) {
        await updateProject({ id: projectId, payload: { ...form, contractFiles: [] } }).unwrap();
        savedProjectId = projectId;
      } else {
        const created = await createProject({ ...form, contractFiles: [] }).unwrap();
        const rawId =
          (created as any)?.data?.id ??
          (created as any)?.id ??
          "";
        savedProjectId = String(rawId);
      }

      // Upload each new contract file individually to /Project/{id}/contracts
      if (newContractFiles.length > 0 && savedProjectId) {
        const errors: string[] = [];
        for (const file of newContractFiles) {
          try {
            await uploadContract({ projectId: savedProjectId, file }).unwrap();
          } catch (err) {
            errors.push(`${file.name}: ${getApiErrorMessage(err as FetchBaseQueryError)}`);
          }
        }
        if (errors.length > 0) {
          setUploadErrors(errors);
          return;
        }
      }

      navigate("/admin/projects");
    } catch (apiError) {
      setSubmitError(getApiErrorMessage(apiError as FetchBaseQueryError));
    }
  };

  const handleNewContractFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextFiles = Array.from(event.target.files ?? []);
    if (nextFiles.length === 0) return;
    setNewContractFiles((prev) => [...prev, ...nextFiles]);
    if (contractInputRef.current) contractInputRef.current.value = "";
  };

  const handleRemoveNewFile = (index: number) => {
    setNewContractFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveServerContract = async (fileId: number) => {
    if (!projectId) return;
    try {
      await deleteContract({ projectId, fileId }).unwrap();
    } catch (err) {
      setSubmitError(getApiErrorMessage(err as FetchBaseQueryError));
    }
  };

  const busy = isCreating || isUpdating || isUploadingContract;
  const fetchErrorMessage = fetchError ? getApiErrorMessage(fetchError as FetchBaseQueryError) : "";

  if (isEditing && isFetchingProject) {
    return <section className="admin-page"><div className="alert alert-light border">Loading project...</div></section>;
  }
  if (isEditing && fetchErrorMessage) {
    return <section className="admin-page"><div className="alert alert-danger">{fetchErrorMessage}</div></section>;
  }

  return (
    <section className="admin-page">
      <div className="container-fluid px-0">
        <div style={{ background: "#fff", borderRadius: 14, border: "1.5px solid #e2e8f0", overflow: "hidden", boxShadow: "0 2px 12px #0000000a" }}>
          {/* Gradient header */}
          <div style={{ background: isEditing ? "linear-gradient(135deg, #78350f, #d97706, #f59e0b)" : "linear-gradient(135deg, #134e4a, #0d9488, #14b8a6)", padding: "10px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <i className={isEditing ? "bi bi-pencil-fill" : "bi bi-kanban-fill"} style={{ color: "#fff", fontSize: 14 }} />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#fff", lineHeight: 1.2 }}>{isEditing ? "Update Project" : "Add New Project"}</div>
                <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.75)" }}>
                  {isEditing ? "Update project details, revise costing, and manage contract files." : "Create a new project in three steps"}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
              <span style={{ background: "rgba(255,255,255,0.2)", color: "#fff", border: "1px solid rgba(255,255,255,0.35)", borderRadius: 20, padding: "3px 12px", fontSize: "0.74rem", fontWeight: 600 }}>
                Step {currentStep + 1} of {steps.length}
              </span>
              <button type="button" style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", borderRadius: 8, padding: "4px 12px", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }} onClick={() => navigate(-1)}>
                <i className="bi bi-arrow-left" /> Back
              </button>
            </div>
          </div>
          <div className="card-body p-4">

            {submitError && <div className="alert alert-danger mb-4">{submitError}</div>}
            {uploadErrors.length > 0 && (
              <div className="alert alert-warning mb-4">
                <strong>Project saved, but some contract files failed to upload:</strong>
                <ul className="mb-0 mt-1">
                  {uploadErrors.map((e) => <li key={e}>{e}</li>)}
                </ul>
              </div>
            )}

            <div className="admin-step-tabs mb-4">
              <div className="nav nav-pills nav-fill flex-column flex-md-row gap-2 mb-3">
                {steps.map((step, index) => (
                  <button
                    key={step}
                    type="button"
                    className={`admin-step-pill${index === currentStep ? " active" : ""}${index < currentStep ? " complete" : ""}`}
                    onClick={() => {
                      if (index <= currentStep) { setCurrentStep(index); return; }
                      if (index === currentStep + 1 && stepValid) setCurrentStep(index);
                    }}
                  >
                    <span className="admin-step-pill-index">{index + 1}</span>
                    <span>{step}</span>
                  </button>
                ))}
              </div>
              <div className="progress admin-step-progress" role="progressbar" title={`Step ${currentStep + 1} of ${steps.length}`} aria-label={`Step ${currentStep + 1} of ${steps.length}`} aria-valuenow={currentStep + 1} aria-valuemin={0} aria-valuemax={steps.length}>
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  style={{ width: `${((currentStep + 1) / steps.length) * 100}%`, background: isEditing ? "linear-gradient(135deg, #d97706, #f59e0b)" : "linear-gradient(135deg, #0d9488, #14b8a6)" }}
                >
                  {Math.round(((currentStep + 1) / steps.length) * 100)}%
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              {currentStep === 0 && (
                <div className="row g-3 align-items-end">
                  <div className="col-md-6">
                    <div style={{ borderLeft: "3px solid #6366f1", paddingLeft: 10 }}>
                      <label className="form-label fw-semibold">
                        Project Title
                        <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Full name of the construction project</small>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ background: "#eef2ff", border: "1.5px solid #c7d2fe", color: "#4f46e5" }}>
                          <i className="bi bi-buildings"></i>
                        </span>
                        <input
                          className="form-control"
                          title="Project title"
                          value={form.title}
                          onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div style={{ borderLeft: "3px solid #0ea5e9", paddingLeft: 10 }}>
                      <label className="form-label fw-semibold">
                        Project Owner
                        <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Client or land owner's full name</small>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ background: "#e0f2fe", border: "1.5px solid #7dd3fc", color: "#0284c7" }}>
                          <i className="bi bi-person"></i>
                        </span>
                        <input
                          className="form-control"
                          title="Project owner name"
                          value={form.owner}
                          onChange={(e) => setForm((p) => ({ ...p, owner: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-12">
                    <div style={{ borderLeft: "3px solid #64748b", paddingLeft: 10 }}>
                      <label className="form-label fw-semibold">
                        Description
                        <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Brief scope, work summary, and execution plan</small>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text align-items-start pt-2" style={{ background: "#f1f5f9", border: "1.5px solid #cbd5e1", color: "#475569" }}>
                          <i className="bi bi-text-left"></i>
                        </span>
                        <textarea
                          className="form-control"
                          rows={4}
                          title="Project description"
                          value={form.description}
                          onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 1 && (
                <div className="row g-3 align-items-end">
                  <div className="col-md-4">
                    <div style={{ borderLeft: "3px solid #10b981", paddingLeft: 10 }}>
                      <label className="form-label fw-semibold">
                        Location
                        <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>City name or full site address</small>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ background: "#d1fae5", border: "1.5px solid #6ee7b7", color: "#047857" }}>
                          <i className="bi bi-geo-alt"></i>
                        </span>
                        <input
                          className="form-control"
                          title="Project location"
                          value={form.location}
                          onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div style={{ borderLeft: "3px solid #f59e0b", paddingLeft: 10 }}>
                      <label className="form-label fw-semibold">
                        Area
                        <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Built-up or plot area, e.g. 4500 sq ft</small>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ background: "#fefce8", border: "1.5px solid #fde68a", color: "#b45309" }}>
                          <i className="bi bi-rulers"></i>
                        </span>
                        <input
                          className="form-control"
                          title="Project area"
                          value={form.area}
                          onChange={(e) => setForm((p) => ({ ...p, area: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div style={{ borderLeft: "3px solid #a855f7", paddingLeft: 10 }}>
                      <label className="form-label fw-semibold">
                        Rate Of Construction
                        <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Construction rate per sq ft, e.g. INR 1800</small>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ background: "#f3e8ff", border: "1.5px solid #d8b4fe", color: "#7c3aed" }}>
                          <i className="bi bi-currency-rupee"></i>
                        </span>
                        <input
                          className="form-control"
                          title="Rate of construction per sq ft"
                          value={form.rateOfConstruction}
                          onChange={(e) => setForm((p) => ({ ...p, rateOfConstruction: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div style={{ borderLeft: "3px solid #f43f5e", paddingLeft: 10 }}>
                      <label className="form-label fw-semibold">
                        Initial Status
                        <small className="d-block fw-normal text-muted" style={{ fontSize: "0.71rem" }}>Current phase of this project</small>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ background: "#fff1f2", border: "1.5px solid #fda4af", color: "#be123c" }}>
                          <i className="bi bi-check-circle"></i>
                        </span>
                        <select
                          className="form-select"
                          value={form.status}
                          onChange={(e) => setForm((p) => ({ ...p, status: e.target.value as ProjectStatus }))}
                          aria-label="Initial project status"
                          title="Initial project status"
                        >
                          <option value="pending">Pending</option>
                          <option value="in-progress">In Progress</option>
                          <option value="completed">Completed</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div className="row g-4">
                  <div className="col-lg-6">
                    <h3 className="h5 mb-3">Contract Upload</h3>

                    {/* Existing server-side contracts (edit mode) */}
                    {isEditing && serverContracts.length > 0 && (
                      <div className="mb-3">
                        <strong className="small text-dark d-block mb-2">Existing Contracts</strong>
                        <div className="list-group">
                          {serverContracts.map((contract) => (
                            <div key={contract.id} className="list-group-item d-flex justify-content-between align-items-center gap-3">
                              <div>
                                <strong className="d-block small text-dark">{contract.fileName}</strong>
                                {contract.uploadedAt && (
                                  <span className="small text-muted">Uploaded: {contract.uploadedAt}</span>
                                )}
                              </div>
                              <div className="d-flex gap-2">
                                {contract.fileUrl && (
                                  <a
                                    href={contract.fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-outline-secondary btn-sm"
                                    title={`Download ${contract.fileName}`}
                                    aria-label={`Download ${contract.fileName}`}
                                  >
                                    <i className="bi bi-download" />
                                  </a>
                                )}
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleRemoveServerContract(contract.id)}
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* New file upload */}
                    <label className="form-label fw-semibold">
                      {isEditing ? "Upload Additional Contract Files" : "Upload Contract Files"}
                    </label>
                    <input
                      ref={contractInputRef}
                      type="file"
                      className="form-control"
                      multiple
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      onChange={handleNewContractFilesChange}
                      aria-label={isEditing ? "Upload additional contract files" : "Upload contract files"}
                      title={isEditing ? "Upload additional contract files" : "Upload contract files"}
                    />
                    <div className="form-text">PDF, DOC, DOCX, JPG, or PNG. Max 50 MB per file.</div>

                    {newContractFiles.length > 0 && (
                      <div className="mt-3">
                        <strong className="small text-dark d-block mb-2">
                          Files Queued for Upload ({newContractFiles.length})
                        </strong>
                        <div className="list-group">
                          {newContractFiles.map((file, index) => (
                            <div key={`${file.name}-${index}`} className="list-group-item d-flex justify-content-between align-items-center gap-3">
                              <div>
                                <strong className="d-block small text-dark">{file.name}</strong>
                                <span className="small text-muted">
                                  {Math.max(1, Math.round(file.size / 1024))} KB
                                </span>
                              </div>
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => handleRemoveNewFile(index)}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {!isEditing && newContractFiles.length === 0 && (
                      <div className="alert alert-light border mt-3 mb-0">No contract file selected yet.</div>
                    )}
                  </div>

                  {/* Project review summary */}
                  <div className="col-lg-6">
                    <div className="card border rounded-4 bg-light-subtle h-100">
                      <div className="card-body">
                        <h3 className="h6 mb-3">Project Review</h3>
                        <div className="small text-muted mb-2"><strong className="text-dark">Title:</strong> {form.title || "—"}</div>
                        <div className="small text-muted mb-2"><strong className="text-dark">Owner:</strong> {form.owner || "—"}</div>
                        <div className="small text-muted mb-2"><strong className="text-dark">Location:</strong> {form.location || "—"}</div>
                        <div className="small text-muted mb-2"><strong className="text-dark">Area:</strong> {form.area || "—"}</div>
                        <div className="small text-muted mb-2"><strong className="text-dark">Rate:</strong> {form.rateOfConstruction || "—"}</div>
                        <div className="small text-muted mb-2"><strong className="text-dark">Status:</strong> {form.status}</div>
                        <div className="small text-muted mb-0">
                          <strong className="text-dark">New contracts:</strong>{" "}
                          {newContractFiles.length > 0
                            ? newContractFiles.map((f) => f.name).join(", ")
                            : "None"}
                        </div>
                        {isEditing && serverContracts.length > 0 && (
                          <div className="small text-muted mt-1">
                            <strong className="text-dark">Existing contracts:</strong> {serverContracts.length} file{serverContracts.length !== 1 ? "s" : ""}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-4">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setCurrentStep((prev) => Math.max(0, prev - 1))}
                  disabled={currentStep === 0 || busy}
                >
                  <i className="bi bi-arrow-left me-1"></i> Previous
                </button>
                <div className="d-flex gap-2">
                  {currentStep < steps.length - 1 ? (
                    <button
                      type="button"
                      style={{ background: isEditing ? "linear-gradient(135deg, #d97706, #f59e0b)" : "linear-gradient(135deg, #0d9488, #14b8a6)", border: "none", color: "#fff", borderRadius: 8, padding: "6px 18px", fontSize: "0.875rem", fontWeight: 600, cursor: (!stepValid || busy) ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", gap: 6, opacity: (!stepValid || busy) ? 0.65 : 1 }}
                      onClick={() => setCurrentStep((prev) => prev + 1)}
                      disabled={!stepValid || busy}
                    >
                      Next Step <i className="bi bi-arrow-right"></i>
                    </button>
                  ) : (
                    <button
                      type="submit"
                      style={{ background: isEditing ? "linear-gradient(135deg, #d97706, #f59e0b)" : "linear-gradient(135deg, #0d9488, #14b8a6)", border: "none", color: "#fff", borderRadius: 8, padding: "6px 18px", fontSize: "0.875rem", fontWeight: 600, cursor: busy ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", gap: 6, opacity: busy ? 0.75 : 1 }}
                      disabled={busy}
                    >
                      {busy ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                          {isUploadingContract ? "Uploading contracts…" : isEditing ? "Updating…" : "Saving…"}
                        </>
                      ) : (
                        <><i className="bi bi-check2"></i> {isEditing ? "Update Project" : "Save Project"}</>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectCreatePage;
